import { useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { getConversationGroup } from "@/api/generated";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { useAuthStore, useUserSentEventStore } from "@/store";
import {
  MESSAGES_PER_PAGE,
  groupMessagesIntoTurns,
  mapEventToClientEvent,
} from "./useInfiniteMessages";

/**
 * Whether the group-aware read path is on. Resolves `keep-conversations-open` to one boolean,
 * treating the loading state as off so nothing reads groups until the flag settles.
 */
export const useKeepConversationsOpen = () => {
  const { value, loading } = useFeatureFlag("keep-conversations-open", false);
  return !loading && Boolean(value);
};

export const groupMessagesQueryKey = (conversationGroupId: string) => [
  "conversationGroup",
  "infinite",
  conversationGroupId,
  MESSAGES_PER_PAGE,
];

/**
 * The whole conversation group's NORI events as one stream. Its query-key namespace is separate
 * from `infiniteMessagesQueryKey`, so the single-conversation writes never touch it.
 */
export const useGroupMessages = (
  conversationGroupId?: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { userSentEvents } = useUserSentEventStore();
  const { isAuthInitialized } = useAuthStore();

  return useInfiniteQuery({
    queryKey: groupMessagesQueryKey(conversationGroupId!),
    queryFn: ({ pageParam, signal }) =>
      getConversationGroup(
        conversationGroupId!,
        { limit: MESSAGES_PER_PAGE, cursor: pageParam },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_cursor,
    retry: (_failCount, error) => (error as AxiosError).status !== 404,
    meta: { suppressErrorToast: true },
    enabled: Boolean(conversationGroupId) && isAuthInitialized && enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 30,
    staleTime: 30000,
    select: (data) => {
      const lastPage = data.pages[data.pages.length - 1];
      const userSentEventStack = [...userSentEvents];

      const allMessages = data.pages
        .flatMap((page) =>
          page.messages.map((message) =>
            mapEventToClientEvent(message, userSentEventStack),
          ),
        )
        .reverse();

      return {
        ...data,
        allMessages,
        allTurns: groupMessagesIntoTurns(allMessages),
        conversationGroup: data.pages[0]?.conversation_group,
        pagination: lastPage?.pagination,
      };
    },
  });
};
